import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, MailCheck } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks/useAuth';
import { emailSchema, signInSchema } from '@/lib/validationSchemas';
import { CURRENT_GENERAL_TERMS_VERSION } from '@/hooks/useTermsAcceptance';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import { cn } from '@/lib/utils';
import { goldGradientActive } from '@/lib/gradientStyles';

const FIELD_SHELL =
	'rounded-lg bg-white/[0.09] border border-white/15 hover:border-white/25 focus-within:border-white/40 focus-within:bg-white/[0.11] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] transition-colors';
const FIELD_INPUT =
	'h-12 bg-transparent border-0 shadow-none focus-visible:ring-0 placeholder:text-white/80 text-base px-4';

export interface AuthFormProps {
	intentRole?: 'buyer' | 'seller';
	/** Called after a successful sign-in / sign-up. */
	onAuthed?: () => void;
	/** Called after a magic link is successfully sent. */
	onMagicSent?: () => void;
	/** Show the "Welcome to CardSimp" hero. Default true. Set false when the parent already provides a title. */
	showHeader?: boolean;
	/** Autofocus email on mount. Default false — callers (desktop modal) opt in. */
	autoFocusEmail?: boolean;
	/** Optional slot rendered inside the form container above the email field. Useful for hosting a rotating subline that should sit inside the glass container in password mode. */
	extraHeader?: React.ReactNode;
	/** When true, hide OAuth row, divider, and legal microcopy to keep the focused input + Continue above the keyboard. */
	compact?: boolean;
}

/**
 * The canonical sign-in / sign-up form. Used by AuthSheet (as a modal Body) and by
 * SignInHome (inlined into the homepage card). Keep all auth UX changes in this file.
 */
export function AuthForm({
	intentRole,
	onAuthed,
	onMagicSent,
	showHeader = true,
	autoFocusEmail = false,
	extraHeader,
	compact = false,
}: AuthFormProps) {
	const { signInOrSignUp, signInWithMagicLink, signInWithGoogle, signInWithApple } = useAuth();

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [rememberMe, setRememberMe] = useState(true);
	const [loading, setLoading] = useState(false);
	const [magicLoading, setMagicLoading] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
	const [appleLoading, setAppleLoading] = useState(false);
	const [showForgot, setShowForgot] = useState(false);
	const [usePassword, setUsePassword] = useState(true);
	const [formError, setFormError] = useState<string | null>(null);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);
	const [magicSentTo, setMagicSentTo] = useState<string | null>(null);
	const emailRef = useRef<HTMLInputElement>(null);

	const anyLoading = loading || magicLoading || googleLoading || appleLoading;
	const clearStatus = () => {
		if (formError) setFormError(null);
		if (successMsg) setSuccessMsg(null);
	};

	useEffect(() => {
		if (!autoFocusEmail) return;
		const t = setTimeout(() => emailRef.current?.focus(), 80);
		return () => clearTimeout(t);
	}, [autoFocusEmail]);

	const handleGoogle = async () => {
		clearStatus();
		setGoogleLoading(true);
		const { error } = await signInWithGoogle();
		if (error) {
			const msg = error.message || 'Failed to sign in with Google';
			setFormError(msg);
			toast.error(msg);
			setGoogleLoading(false);
		}
		// On success the browser redirects to Google; no local state to update.
	};

	const handleApple = async () => {
		clearStatus();
		setAppleLoading(true);
		const { error } = await signInWithApple();
		if (error) {
			const msg = error.message || 'Failed to sign in with Apple';
			setFormError(msg);
			toast.error(msg);
			setAppleLoading(false);
		}
	};

	const handleContinue = async (e: React.FormEvent) => {
		e.preventDefault();
		clearStatus();
		const validation = signInSchema.safeParse({ email, password });
		if (!validation.success) {
			const msg = validation.error.errors[0].message;
			setFormError(msg);
			toast.error(msg);
			return;
		}
		setLoading(true);
		const { error, didSignUp } = await signInOrSignUp(
			validation.data.email,
			validation.data.password,
			rememberMe,
			{ generalTermsVersion: CURRENT_GENERAL_TERMS_VERSION },
			intentRole,
		);
		setLoading(false);
		if (error) {
			setFormError(error.message);
			toast.error(error.message);
			return;
		}
		if (didSignUp) {
			setSuccessMsg('Account created — signing you in…');
			toast.success('Welcome to CardSimp', {
				description: "Check your email if we asked you to confirm.",
				duration: 6000,
			});
		} else {
			setSuccessMsg('Signed in — taking you in…');
		}
		setEmail('');
		setPassword('');
		onAuthed?.();
	};

	const handleMagicLink = async () => {
		clearStatus();
		const validation = emailSchema.safeParse(email);
		if (!validation.success) {
			const msg = 'Enter a valid email first';
			setFormError(msg);
			toast.error(msg);
			emailRef.current?.focus();
			return;
		}
		setMagicLoading(true);
		const { error } = await signInWithMagicLink(
			validation.data,
			{ generalTermsVersion: CURRENT_GENERAL_TERMS_VERSION },
			intentRole,
		);
		setMagicLoading(false);
		if (error) {
			setFormError(error.message);
			toast.error(error.message);
			return;
		}
		setMagicSentTo(validation.data);
		onMagicSent?.();
	};

	return (
		<>
			<div className="flex flex-col gap-5 transition-all duration-300">
				{showHeader && !compact && (
					<div className="text-center space-y-1.5">
						<h2 className="tracking-tight leading-none">
							<span className="font-sans text-3xl font-bold text-muted-foreground mr-1 [text-shadow:0_1px_2px_rgba(0,0,0,0.55),0_2px_10px_rgba(0,0,0,0.35)]">
								Welcome to
							</span>
							<span className="font-logo text-3xl font-bold [text-shadow:0_1px_2px_rgba(0,0,0,0.55),0_2px_10px_rgba(0,0,0,0.35)]">
								<span className="text-foreground">Card</span>
								<span className="text-primary">Simp</span>
							</span>
						</h2>
						<p className="text-xs text-muted-foreground">
							Sign in — we'll create your account if it's your first time.
						</p>
					</div>
				)}
				{extraHeader}

				{magicSentTo ? (
					<div
						role="status"
						aria-live="polite"
						className="rounded-xl border border-active-gold/40 bg-active-gold/10 p-4 flex flex-col items-center text-center gap-2"
					>
						<MailCheck className="h-8 w-8 text-active-gold" aria-hidden />
						<div className="text-sm font-semibold text-foreground">Check your inbox</div>
						<div className="text-xs text-muted-foreground leading-relaxed">
							We sent a sign-in link to{' '}
							<span className="text-foreground font-medium break-all">{magicSentTo}</span>.
							Tap it to finish signing in.
						</div>
						<button
							type="button"
							onClick={() => {
								setMagicSentTo(null);
								setFormError(null);
							}}
							className="mt-1 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
						>
							Use a different email
						</button>
					</div>
				) : (
					<>
				{formError && (
					<div
						role="alert"
						aria-live="assertive"
						className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 flex items-start gap-2 text-sm text-destructive-foreground"
					>
						<AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden />
						<span className="leading-snug">{formError}</span>
					</div>
				)}
				{successMsg && !formError && (
					<div
						role="status"
						aria-live="polite"
						className="rounded-lg border border-active-gold/40 bg-active-gold/10 px-3 py-2.5 flex items-center gap-2 text-sm text-foreground"
					>
						<CheckCircle2 className="h-4 w-4 shrink-0 text-active-gold" aria-hidden />
						<span className="leading-snug">{successMsg}</span>
					</div>
				)}

				<form
					onSubmit={(e) => {
						if (usePassword) {
							handleContinue(e);
						} else {
							e.preventDefault();
							handleMagicLink();
						}
					}}
					className="space-y-3"
				>
					<div className={FIELD_SHELL}>
						<Input
							ref={emailRef}
							type="email"
							name="email"
							autoComplete="email"
							inputMode="email"
							enterKeyHint={usePassword ? 'next' : 'go'}
							placeholder="your@email.com"
							value={email}
							onChange={(e) => { setEmail(e.target.value); clearStatus(); }}
							className={FIELD_INPUT}
							required
							aria-label="Email"
						/>
					</div>
					{usePassword && (
						<>
							<div className={cn(FIELD_SHELL, 'relative')}>
								<Input
									type={showPassword ? 'text' : 'password'}
									name="password"
									autoComplete="current-password"
									enterKeyHint="go"
									placeholder="Password (min. 8 characters)"
									value={password}
									onChange={(e) => { setPassword(e.target.value); clearStatus(); }}
									className={cn(FIELD_INPUT, 'pr-11')}
									required
									aria-label="Password"
								/>
								<button
									type="button"
									onMouseDown={(e) => {
										e.preventDefault();
										setShowPassword((v) => !v);
									}}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
									aria-label="Toggle password visibility"
								>
									{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</button>
							</div>

							<div className="flex items-center justify-between text-sm">
								<label className="flex items-center gap-2 cursor-pointer text-muted-foreground">
									<Checkbox
										checked={rememberMe}
										onCheckedChange={(c) => setRememberMe(c === true)}
									/>
									Remember me
								</label>
								<button
									type="button"
									onClick={() => setShowForgot(true)}
									className="text-xs text-foreground/80 hover:text-foreground underline-offset-2 hover:underline"
								>
									Forgot password?
								</button>
							</div>
						</>
					)}

					<Button
						type="submit"
						className={cn(
							'w-full h-12 text-base font-semibold tracking-wide',
							'text-[#1a1408]',
							'bg-gradient-to-b from-active-gold to-[hsl(45,95%,58%)]',
							'border border-active-gold/60',
							'shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(0,0,0,0.18),0_5px_14px_-8px_hsl(50_100%_50%/0.22)]',
							'hover:brightness-105 active:brightness-95 transition'
						)}
						disabled={anyLoading}
					>
						{(loading || magicLoading) && (
							<Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
						)}
						{usePassword
							? (loading ? 'Signing in…' : 'Continue')
							: (magicLoading ? 'Sending link…' : 'Continue')}
					</Button>
				</form>

				<button
					type="button"
					onClick={() => {
						setUsePassword((v) => {
							if (v) setPassword('');
							return !v;
						});
					}}
					disabled={loading || magicLoading}
					className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{usePassword ? 'Email me a sign-in link instead' : 'Use a password instead'}
				</button>

				{!compact && (
					<>
						<div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground/70">
							<div className="h-px flex-1 bg-white/[0.08]" />
							<span>or</span>
							<div className="h-px flex-1 bg-white/[0.08]" />
						</div>

						<div className="flex flex-col gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={handleGoogle}
								disabled={loading || magicLoading || googleLoading || appleLoading}
								className="w-full h-12 gap-3 bg-white/[0.05] border-white/[0.08] hover:bg-white/[0.08] hover:border-white/30 text-foreground"
							>
								{googleLoading ? (
									<Loader2 className="h-5 w-5 animate-spin" aria-hidden />
								) : (
									<svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
										<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
										<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
										<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
										<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
									</svg>
								)}
								{googleLoading ? 'Opening Google…' : 'Continue with Google'}
							</Button>

							<Button
								type="button"
								variant="outline"
								onClick={handleApple}
								disabled={loading || magicLoading || googleLoading || appleLoading}
								className="w-full h-12 gap-3 bg-white/[0.05] border-white/[0.08] hover:bg-white/[0.08] hover:border-white/30 text-foreground"
							>
								{appleLoading ? (
									<Loader2 className="h-5 w-5 animate-spin" aria-hidden />
								) : (
									<svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
										<path d="M17.564 12.91c-.027-2.76 2.255-4.083 2.358-4.148-1.284-1.878-3.283-2.134-3.997-2.165-1.704-.173-3.324 1.003-4.19 1.003-.866 0-2.198-.977-3.61-.95-1.859.027-3.572 1.08-4.527 2.744-1.93 3.346-.494 8.298 1.388 11.016.92 1.33 2.018 2.825 3.458 2.772 1.388-.056 1.913-.9 3.591-.9 1.678 0 2.149.9 3.618.87 1.493-.025 2.44-1.357 3.354-2.692 1.057-1.546 1.493-3.043 1.52-3.12-.033-.014-2.92-1.12-2.963-4.43zM14.85 4.93c.764-.93 1.28-2.22 1.139-3.504-1.102.045-2.435.735-3.225 1.661-.71.82-1.331 2.131-1.164 3.393 1.228.094 2.486-.62 3.25-1.55z" />
									</svg>
								)}
								{appleLoading ? 'Opening Apple…' : 'Continue with Apple'}
							</Button>
						</div>
					</>
				)}
					</>
				)}



				{!compact && (
					<p className="text-[11px] text-muted-foreground/80 text-center leading-relaxed pt-2">
						By continuing you agree to our{' '}
						<Link to="/terms" target="_blank" className="underline hover:text-foreground">
							Terms
						</Link>
						,{' '}
						<Link to="/privacy" target="_blank" className="underline hover:text-foreground">
							Privacy
						</Link>{' '}
						and{' '}
						<Link to="/safety" target="_blank" className="underline hover:text-foreground">
							Safety Guide
						</Link>
						. 18+.
					</p>
				)}
			</div>
			<ForgotPasswordDialog open={showForgot} onOpenChange={setShowForgot} />
		</>
	);
}

export default AuthForm;
